import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDraggable } from '@dnd-kit/core';
import {
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {
  IEntityNode,
  ISelectedEntity,
} from '../../Builder/EntitySelection/types';
import { EntityDropzoneType } from '../DictionaryTabs/types';
import DictionaryStyledDragItem from '../DictionaryStyledDragItem';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { useFloatingLayerContext } from '../../FloatingManager';
import DictionaryEntityDetails from '../DictionaryEntityDetails';
import Loading from '../../Builder/EntitySelection/panel/Loading';
import { useCurrentUrl } from '../../../utils/routing';

// Component has 2 built-in features:
// 1. It acts like a draggable item;
// 2. It will render Entity details popover on click

interface IProps {
  id: string;
  type: EntityDropzoneType | null;
  node: IEntityNode;
  schemaId: string;
  entity: ISelectedEntity | null;
  onOpen: () => void;
  onClose: () => void;
  onRefresh: () => void;
}

const Container = styled.div`
  outline: none;
`;
const FloatingPopover = styled.div`
  width: 220px;
  min-height: 50px;
  padding: 8px;
  ${floatingComponentStyles};
`;

const DictionaryEntityNode = ({
  id,
  type,
  node,
  schemaId,
  entity,
  onOpen,
  onClose,
  onRefresh,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { isDashboardInUrl } = useCurrentUrl();
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);

      if (open) {
        onOpen();
      } else {
        onClose();
      }
    },
    middleware: [offset(5)],
    placement: 'left',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: {
      type,
      data: node,
    },
    disabled: isDashboardInUrl,
  });

  const zIndex = useFloatingLayerContext();

  const handleRefreshDetails = useCallback(() => {
    onRefresh();
    setIsOpen(false);
  }, [onRefresh]);

  // when we are dragging, close popup
  useEffect(() => {
    if (isDragging && isOpen) {
      onClose();
      setIsOpen(false);
    }
  }, [isDragging, isOpen, onClose]);

  return (
    <>
      <Container ref={setNodeRef} {...attributes} {...listeners}>
        <DictionaryStyledDragItem
          ref={refs.setReference}
          isActive={isOpen}
          node={node}
          {...getReferenceProps()}
        >
          {node.name}
        </DictionaryStyledDragItem>
      </Container>
      {isOpen && (
        <FloatingPortal>
          <FloatingPopover
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex }}
            {...getFloatingProps()}
          >
            {entity ? (
              <DictionaryEntityDetails
                schemaId={schemaId}
                entity={entity}
                node={node}
                onRefresh={handleRefreshDetails}
              />
            ) : (
              <Loading />
            )}
          </FloatingPopover>
        </FloatingPortal>
      )}
    </>
  );
};

export default DictionaryEntityNode;
